<script type="text/ecmascript-6">
import { mapActions, mapGetters } from "vuex";
import GeneralMixin from "JS/mixins/campaign/general";
import LegislatorsMixin from "JS/mixins/campaign/legislators";
import FormValidationMixin from "JS/mixins/campaign/formValidation";
import StringFilters from "JS/filters/stringFilters";
import {formatParenthesis} from "../../../helpers/textFormatters";
import {
  difference as _difference,
  forEach as _forEach,
  forOwn as _forOwn,
  find as _find,
  get as _get,
  isEqual as _isEqual,
  isEmpty as _isEmpty,
  keys as _keys,
  map as _map
} from "lodash";
import {
  AxiosCreateAdvocate,
  AxiosCreatePreviews,
  AxiosOneStepTakeAction,
  AxiosTakeAction,
  SocialApi
} from "Api/public/axios";

export default {
  name: "FormSubmit",
  filters: StringFilters,
  mixins: [GeneralMixin, FormValidationMixin, LegislatorsMixin],
  props: {
    advocateMessageRequiredPrompt: {
      required: true,
      type: String
    },
    andLabel: {
      required: true,
      type: String
    },
    appSettings: {
      required: true,
      type: Object
    },
    callProgressButtonLabel: {
      required: true,
      type: String
    },
    cookieButtonLabels: {
      required: true,
      type: Object
    },
    formFields: {
      required: true,
      type: Object
    },
    formStyles: {
      type: Object,
      default: () => {}
    },
    advocateCreateButtonText: {
      required: true,
      type: Object
    },
    isMultistep: {
      type: Boolean,
      required: true
    },
    loadStart: {
      type: String,
      default: ""
    },
    getStartedString: {
      type: String,
      default: "Get Started"
    },
    nextStepString: {
      type: String,
      default: "Next Step"
    },
    noLegislatorsFoundErrorMessage: {
      type: String,
      required: true
    },
    submitButtonClass: {
      required: true,
      type: Object
    },
    preventReturnAdvocateAction: {
      type: Boolean,
      required: true
    },
    submitButtonLabels: {
      required: true,
      type: Object
    },
    campaignDesignColors: {
      required: true,
      type: Object
    },
    previewMode: {
      required: true,
      type: Boolean
    },
    previewModeAlert: {
      required: true,
      type: String
    },
    privacyPolicyLabel: {
      required: true,
      type: String
    },
    customerName: {
      required: true,
      type: String
    },
    displayName: {
      default: "",
      type: String
    },
    defaultPrivacyConsentLanguage: {
      required: true,
      type: String
    },
    defaultPrivacyConsentLanguageAccordance: {
      required: true,
      type: String
    },
    invalidFormFieldsErrorMessage: {
      required: true,
      type: String
    },
    privacyConsentLanguage: {
      required: true,
      type: String
    },
    hasOneStep: {
      type: Boolean,
      required: true
    },
    hidePrivacyPolicy: {
      required: true,
      type: Number
    },
    privacyPolicyUrl: {
      default: "",
      type: String
    },
    privacyPolicyUrlLabel: {
      default: "",
      type: String
    },
    showPrivacyConsentLanguage: {
      required: true,
      type: Number
    },
    shouldShowSubmitButtons: {
      required: true,
      type: Object
    },
    buttonEmailReverse: {
      required: true,
      type: Boolean
    },
    isMobile: {
      type: Boolean,
      default: false
    },
    takeActionLabel: {
      type: String,
      default: "Take Action"
    },
    skipLabel: {
      type: String,
      default: "Skip"
    },
    socialShare: {
      type: Object,
      default: () => {}
    },
    stepsWithSubscription: {
      type: Array,
      default: () => []
    },
    p2aSourceId: {
      type: String,
      default: ""
    },
    advocateShortUrlId: {
      type: String,
      default: ""
    },
    useTwitterWebIntents: {
      type: Boolean,
      required: true
    },
    emailEditRequired: {
      type: Boolean,
      required: true
    },
    hideSendButtonIcon: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data() {
    return {
      context: "forms",
      showButtonHover: false
    };
  },
  computed: {
    ...mapGetters({
      activeStep: "navigation/activeStep", // todo: lets kill this and use currentStep
      activeAction: "navigation/activeAction",
      advocateId: "advocate/id",
      callInProgress: "legislators/callInProgress",
      callsAllCompleted: "legislators/callsCompleted",
      cookieBadgeVisible: "forms/cookieBadgeVisible",
      customFields: "forms/customFields",
      currentStepInAction: "navigation/currentActionStep",
      currentMessage: "legislators/currentMessage",
      descriptionVisible: "navigation/campaignDescriptionVisible",
      errorMessage: "forms/error",
      legislatorErrorMessage: "legislators/errorMessage",
      loading: "navigation/actionLoading",
      twitterCredentialsSaved: "advocate/twitterCredentialsSaved",
      emailMessageModified: "legislators/emailMessageModified"
    }),
    activeStepHasSubscription() {
      return this.stepsWithSubscription.indexOf(this.activeStep) > -1;
    },
    actionHasMoreMessages() {
      const isActionCard =
        this.isActionCard("email") || this.isActionCard("twitter");
      const moreMessagesExist =
        Object.keys(this.legislatorMessages).length - 1 > this.currentMessage;

      return isActionCard && moreMessagesExist;
    },
    advocateCreatePayload() {
      return this.$store.getters[`${this.context}/advocateCreatePayload`];
    },
    advocateRecord() {
      return this.$store.getters[`${this.context}/advocateRecord`];
    },
    currentActionTaken() {
      return !_isEmpty(this.completedActions[this.currentAction]);
    },
    createAdvocateOnStep() {
      return !this.nextStep;
    },
    currentStepInActionConfig() {
      if (!this.currentStepInAction) {
        return {};
      }
      return _find(
        this.currentActionStepsConfig,
        stepConfig => stepConfig.name === this.currentStepInAction
      );
    },
    currentStepInActionLabel() {
      return this.currentStepInActionConfig.label || "Next Step";
    },
    currentStepTakesAction() {
      return this.currentStepInActionConfig.takeAction === true;
    },
    currentActionDetails() {
      return this.actionDetails[this.currentAction];
    },
    currentActionStepsConfig() {
      return this.currentActionDetails.steps;
    },
    currentActionSteps() {
      return _map(this.currentActionStepsConfig, config => config.name);
    },
    actionFieldSteps() {
      return _keys(this.formFields[this.currentAction]);
    },
    legislatorMessages() {
      return this.legislatorResults[this.currentAction] || [];
    },
    needAdditionalFields() {
      return this.$store.getters["forms/needAdditionalFields"][
        this.currentAction
      ];
    },
    nextStepLabel() {
      // Map of conditions and corresponding values
      const labels = new Map([
        [
          this.cookieBadgeVisible && !this.descriptionVisible,
          this.cookieButtonLabels.yes
        ],
        [this.descriptionVisible, this.getStartedString],
        [this.legislatorsPulled, this.currentStepInActionLabel],
        [this.nextStepString, this.nextStepString]
      ]);
      const label = Array.from(labels.entries()).find(
        condition => condition[0]
      );
      return label[1];
    },
    nextStepIndex() {
      return this.actionFieldSteps.indexOf(this.activeStep) + 1;
    },
    nextStep() {
      return this.nextStepIndex > -1
        ? this.actionFieldSteps[this.nextStepIndex]
        : null;
    },
    nextStepInAction() {
      const nextActionStepIndex =
        this.currentStepInAction === undefined
          ? 0
          : this.currentActionSteps.indexOf(this.currentStepInAction) + 1;

      return this.currentActionSteps[nextActionStepIndex];
    },
    shouldStoreTwitterCredentials() {
      const isTwitterEnabled = this.enabledActions.indexOf("twitter") !== -1;
      const isTwitterActionTaken =
        this.completedActions.indexOf("twitter") !== -1;
      const advocateId = this.advocateId && this.advocateId.toString();
      return (
        !this.twitterCredentialsSaved &&
        isTwitterEnabled &&
        !_isEmpty(advocateId) &&
        !isTwitterActionTaken
      );
    },
    showAdvocateCreateButton() {
      return (
        (!this.legislatorsPulled || this.needAdditionalFields) &&
        !this.showOneStep
      );
    },
    showOneStep() {
      return this.hasOneStep && this.currentAction === "email";
    },
    showErrorMessage() {
      const isNoLegislatorsFoundError =
        this.errorMessage === this.noLegislatorsFoundErrorMessage;
      return (
        !_isEmpty(this.errorMessage) &&
        (this.showOneStep || !isNoLegislatorsFoundError)
      ); // only show no legislators found error on one step
    },
    // Set the action button label after the advocate submits their info
    // TODO: The phone number formatting is a temporary solution while validation is broken - we should reexamine when validation fixed.
    submitButtonLabelText() {
      if (!this.legislatorsPulled && !this.showOneStep) {
        return "";
      }
      const buttonLabelProp = this.submitButtonLabels[this.currentAction];

      return this.currentAction === "call"
        ? this.callButtonLabelText(buttonLabelProp)
        : buttonLabelProp;
    },
    submitDisabled() {
      if (this.loading) {
        return true;
      }
      if (this.errorMessage || this.legislatorErrorMessage) {
        return true;
      }
      // if conversational, we want the button to work to end call early
      if (
        this.currentAction === "call" &&
        this.callInProgress &&
        this.currentStepInAction !== "inprogress"
      ) {
        return true;
      } else if (this.currentAction === "twitter" && this.hasInvalidTweets()) {
        return true;
      } else if (
        this.currentAction === "email" &&
        this.emailEditRequired &&
        !this.emailMessageModified &&
        (this.legislatorsPulled || this.showOneStep)
      ) {
        return true;
      }
      return this.currentAction !== "call" && this.hasEmptyMessage;
    },
    disableNextStepButton() {
      return (
        !this.cookieBadgeVisible &&
        (!this.isFormValid || this.submitDisabled || this.geosearchErrorExists)
      );
    },
    hasEmptyMessage() {
      const messageWithEmptyBody =
        _find(this.legislatorMessages, legislatorMessage => {
          const messageBody = legislatorMessage.message.body || "";
          return !messageBody.trim().length;
        }) || {};
      return !_isEmpty(messageWithEmptyBody);
    },
    submitButtonColors() {
      const buttonColors = {
        background: this.campaignDesignColors.submitButtonBackground,
        color: this.campaignDesignColors.submitButtonText
      };

      if (this.showButtonHover && !this.isMobile) {
        buttonColors.background = this.campaignDesignColors.submitButtonHover;
      }

      return buttonColors;
    },
    consentLanguage() {
      if (this.privacyConsentLanguage) {
        return this.privacyConsentLanguage;
      }
      const name = this.displayName || this.customerName;
      const accordance =
        this.hidePrivacyPolicy === 0 || this.hasCustomPrivacyText
          ? this.defaultPrivacyConsentLanguageAccordance
          : "";
      return `${name} ${this.defaultPrivacyConsentLanguage} ${accordance}`;
    },
    hasCustomPrivacyText() {
      return !!(this.privacyPolicyUrl && this.privacyPolicyUrlLabel);
    },
    displayPrivacyConsentLanguage() {
      return !this.completedAllActions && this.showPrivacyConsentLanguage === 1;
    },
    advocateCreateButtonActionText() {
      return this.advocateCreateButtonText[this.currentAction];
    },
    showSubmitButton() {
      if (this.currentAction === "default") {
        return true;
      } else if (
        this.currentAction === "twitter" &&
        this.useTwitterWebIntents &&
        this.legislatorsPulled
      ) {
        return false;
      }

      return this.shouldShowSubmitButtons[this.currentAction];
    },
    reverseButtonEmailStyle() {
      // temporarily solution for buttonEmailReverse setting,
      // when the setting is on, move email button to the bottom by
      // changing the order to magic number 7,
      // TODO: consider remove this backend setting and use custom css
      if (this.currentAction === "email" && this.buttonEmailReverse) {
        return { order: 7 };
      }
      return {};
    }
  },
  inject: {
    axiosHelper: { from: "axiosHelper" },
    eventsBus: { from: "eventsBus" },
    twitterHelper: { from: "twitterHelper" }
  },
  watch: {
    callInProgress() {
      if (this.callInProgress === true) {
        this.toggleActionLoad(false);
        this.incrementStepInAction();
      }
    },
    callsAllCompleted(val) {
      if (val && this.currentAction === "call") {
        this.$store.dispatch("legislators/callInProgress", false);
        this.$store.dispatch("navigation/actionCompleted", "call");
      }
    },
    disableNextStepButton(disabled) {
      if (disabled) {
        this.assignSubmitHoverColor(false);
      }
    },
    async tweetsPosted(tweets, postedTweets) {
      const newTweets = _difference(tweets, postedTweets);
      const numTweetsPosted = tweets.length;
      const numLegislatorMessages = Object.keys(this.legislatorMessages).length;
      const allTweetsHaveBeenPosted = numTweetsPosted === numLegislatorMessages;

      if (this.useTwitterWebIntents) {
        for await (let tweet of newTweets) {
          // create preview records for newly posted tweets
          await this.createPreviews(tweet);
        }

        if (allTweetsHaveBeenPosted) {
          // complete the action
          this.takeAction();
        } else {
          // mark preview records as completed
          this.sendTakeActionPostRequest();
          this.goToNextStep();
        }
      }
    }
  },
  mounted() {
    this.onMounted();
  },
  methods: {
    ...mapActions({
      incrementActiveStepNumber: "navigation/incrementActiveStepNumber",
      setActiveStep: "navigation/activeStep",
      setCookieBadgeVisible: "forms/cookieBadgeVisible",
      setCampaignDescriptionCard: "navigation/campaignDescriptionVisible",
      setCurrentStepInAction: "navigation/currentActionStep",
      setCurrentMessage: "legislators/currentMessage",
      setFormError: "forms/error",
      setLegislatorErrorMessage: "legislators/errorMessage",
      setTwitterCredentialsSaved: "advocate/twitterCredentialsSaved",
      setAdvocateSubmittedInfo: "forms/advocateRecord"
    }),
    kebabCaseHelper(string) {
      return this.$options.filters.kebabCase(string);
    },
    onMounted() {
      this.eventsBus.$on("skipStep", skipIncrementStepNumber =>
        this.goToNextStep(skipIncrementStepNumber)
      );
      this.eventsBus.$on("actionActivated", () => {
        this.setCurrentStepInAction(null);
        this.setFormError("");
      });
      this.eventsBus.$on("createOrUpdateAdvocate", payload => {
        this.createAdvocate(payload.formValues);
      });
      this.eventsBus.$on("handleFormFieldValue", () => {
        this.setFormError("");
        this.setLegislatorErrorMessage("");
      });
    },
    authorizeTwitter() {
      // @TODO pass in short_url from prop
      return this.openPopup(`/twitter/oauth/redirect`);
    },
    callButtonLabelText(buttonLabelProp) {
      if (this.callInProgress) {
        // TODO - need to add translation for prop string
        return this.callProgressButtonLabel;
      }
      const { phone } = this.formValues;
      const formattedPhone = this.formatPhone(phone);
      return `${buttonLabelProp} ${formattedPhone}`;
    },
    async takeOneStepAction() {
      let legislatorMessages = this.transformLegislatorMessagesFromStore();
  
      if (!this.checkValidation()) {
        this.toggleActionLoad(false);
        return this.errorMessage;
      }
      
      if (_isEmpty(legislatorMessages.legislators)) {
        this.toggleActionLoad(true);
        await this.geosearchLegislators(this.getAdvocateAddress(this.formValues));
        legislatorMessages = this.transformLegislatorMessagesFromStore();
      }
  
      if (_isEmpty(legislatorMessages.legislators)) {
        this.toggleActionLoad(false);
        return;
      }
      
      this.toggleActionLoad(true);

      const action = this.currentAction;
      const { p2aSourceId, advocateShortUrlId } = this;
      const payload = {
        action,
        p2aSourceId,
        advocateShortUrlId,
        ...this.getAdvocateCreateOrUpdatePayload(this.formValues),
        ...legislatorMessages
      };

      try {
        const response = await AxiosOneStepTakeAction.post("", payload);

        const { advocate, actionsTaken } = response.data;
        advocate.actionsTaken = actionsTaken;
        this.setAdvocateSubmittedInfo(advocate);
        this.handleAfterAdvocateCreate(advocate, this.formValues);

        const afterTakeAction = this.handleAfterTakeAction(response.data);
        await this.handleStoreTwitterCredentials();

        // don't kill the action load on call until call is actually made
        if (this.currentAction !== "call") {
          this.toggleActionLoad(false);
        }

        return afterTakeAction;
      } catch (error) {
        const errorMessage = this.axiosHelper.retrieveErrorMessage(error);
        this.setFormError(errorMessage);
        if (this.setCampaignRestrictionError(error)) {
          // if we get restricted, show thankyou component with error
          this.$store.dispatch("navigation/completedAllActions");
        }
        this.eventsBus.$emit("actionFailed", {
          action,
          error: errorMessage
        });
        this.toggleActionLoad(false);
        return errorMessage;
      }
    },
    async createAdvocate(formValues = {}) {
      this.toggleActionLoad(true);
      this.setFormError("");
      try {
        const advocate = await this.postCreateAdvocate(formValues);
        this.handleAfterAdvocateCreate(advocate, formValues);
        await this.handleStoreTwitterCredentials();
        this.toggleActionLoad(false);
        return advocate;
      } catch (error) {
        const errorCode = this.axiosHelper.getErrorStatusCode(error);
        const errorMessage = this.axiosHelper.retrieveErrorMessage(error);

        this.toggleActionLoad(false);

        // special case for invalid token status code
        // (dont set an error because we just need a new one)
        if (errorCode !== 498) {
          this.setFormError(errorMessage);
          if (this.setCampaignRestrictionError(error) && !this.isMultistep) {
            // if we get restricted, show thankyou component with error
            this.$store.dispatch("navigation/completedAllActions");
          }
        }

        if (errorCode === 403 || errorCode === 422) {
          this.$store.dispatch("forms/actionError", errorMessage);
          this.showErrorModal();
        }
        return errorMessage;
      }
    },
    emitSaveFrozenFields() {
      this.eventsBus.$emit("attemptedTakeAction", {
        currentAction: this.currentAction
      });
    },
    async goToNextStep(skipIncrementStepNumber = false) {
      if (this.descriptionVisible) {
        this.setCampaignDescriptionCard(false);
        return;
      } else if (this.cookieBadgeVisible) {
        this.setCookieBadgeVisible(false);
        return;
      }

      if (!this.currentStepInAction) {
        if (!this.isFormValid && !this.isFormSkippable) {
          return;
        }
        this.incrementStep(skipIncrementStepNumber);
      } else {
        this.goToNextActionStep();
      }
    },
    goToNextActionStep() {
      if (this.actionHasMoreMessages) {
        this.incrementActionMessage();
      } else if (this.currentStepTakesAction) {
        this.handleTakeAction();
      } else if (this.nextStepInAction) {
        this.incrementStepInAction();
      } else {
        this.markActionCompleted();
      }
    },
    async incrementStep(skipIncrementStepNumber = false) {
      let advocateCreated = false;
      if (this.createAdvocateOnStep) {
        advocateCreated = await this.handleAdvocateCreation();
      }

      if (!this.nextStep) {
        if (advocateCreated) {
          this.setActiveStep(null);
          this.incrementStepInAction();
        }
        return;
      }

      this.emitSaveFrozenFields();

      if (!skipIncrementStepNumber && this.activeStep !== "welcome") {
        this.incrementActiveStepNumber();
      }

      this.setActiveStep(this.nextStep);
    },
    incrementActionMessage() {
      this.setCurrentMessage(this.currentMessage + 1);
    },
    incrementStepInAction() {
      this.emitSaveFrozenFields();

      this.setCurrentStepInAction(this.nextStepInAction);
    },
    preventReturnAdvocateActions(
      advocate = { actionsTaken: [], joinedCampaign: false }
    ) {
      if (!this.preventReturnAdvocateAction) {
        return;
      }

      const numActionsTaken = advocate.actionsTaken.length;

      if (
        (numActionsTaken > 0 &&
          numActionsTaken === this.enabledActions.length) ||
        (this.activeAction === "default" && advocate.joinedCampaign)
      ) {
        this.$store.dispatch("navigation/allActionsAlreadyTaken", true);
        this.$store.dispatch("navigation/completedAllActions");
      }
    },
    handleAfterAdvocateCreate(advocate, formValues) {
      this.$store.dispatch("advocate/id", advocate.id);
      this.$store.dispatch("advocate/firstName", advocate.firstname);
      this.$store.dispatch("advocate/lastName", advocate.lastname);

      this.emitSaveFrozenFields();

      if (this.currentActionTaken) {
        return;
      }

      this.preventReturnAdvocateActions(advocate);

      if (this.currentAction === "default") {
        this.$store.dispatch("navigation/actionCompleted", this.currentAction);
      } else if (!this.nextStep) {
        this.eventsBus.$emit("findLegislators", {
          background: false,
          address: this.getAdvocateAddress(formValues)
        });
      }

      if (!this.nextStep) {
        this.$store.dispatch(`forms/needAdditionalFields`, {
          action: this.currentAction,
          needAdditionalFields: false
        });
      }
    },
    checkValidation() {
      this.eventsBus.$emit("checkFormValidation");

      if (this.previewMode) {
        this.setFormError(this.previewModeAlert);
        return false;
      }

      if (!this.isFormValid && !this.isFormSkippable) {
        this.setFormError(this.invalidFormFieldsErrorMessage);
        return false;
      }

      return true;
    },
    createPreviews(messageId = null) {
      return AxiosCreatePreviews.post("", {
        action: this.currentAction,
        advocateid: this.advocateId,
        p2aSourceId: this.p2aSourceId,
        advocateShortUrlId: this.advocateShortUrlId,
        address: this.getAdvocateAddress(this.formValues),
        messageId
      });
    },
    async handleAdvocateCreation() {
      if (!this.checkValidation()) {
        return false;
      }

      this.toggleActionLoad(true);

      const advocate = await this.createAdvocate(this.formValues);

      return typeof advocate !== "string";
    },
    async handleTakeAction() {
      this.toggleActionLoad(true);
      this.setFormError("");

      switch (this.currentAction) {
        case "twitter":
          this.twitterAction();
          break;
        case "email":
          this.emailAction();
          break;
        default:
          this.takeAction();
          break;
      }
    },
    emailAction() {
      return this.hasOneStep
        ? this.takeOneStepAction()
        : this.createPreviewsAndTakeAction();
    },
    hasInvalidTweets() {
      return false;
    },
    async postCreateAdvocate(formValues) {
      const payload = this.getAdvocateCreateOrUpdatePayload(formValues);

      if (this.shouldCreateOrUpdateAdvocate(payload)) {
        const createOrUpdateResponse = await AxiosCreateAdvocate.post(
          "",
          payload
        );
        this.$store.dispatch(
          `${this.context}/advocateRecord`,
          createOrUpdateResponse.data
        );
        this.$store.dispatch(`${this.context}/advocateCreatePayload`, payload);
      }

      return this.advocateRecord;
    },
    getAdvocateCreateOrUpdatePayload(formValues) {
      const address = this.getAdvocateAddress(formValues);
      if (_isEmpty(formValues.zipcode)) {
        address.zipcode = this.loadStart;
      }

      const advocateParameters = {
        identifiers: {
          phone: "",
          email: ""
        },
        title: formValues.title,
        name: formValues.fullName,
        firstName: formValues.firstName,
        lastName: formValues.lastName,
        address,
        customFields: this.getStateCustomFieldsValues()
      };

      const { p2aSourceId, advocateShortUrlId } = this;
      const devices = this.getAdvocateDevices(formValues);

      if (_isEmpty(devices)) {
        return advocateParameters;
      }

      const emailDevice = devices.email || {};
      const phoneDevice = devices.phone || {};

      return {
        ...advocateParameters,
        identifiers: {
          phone: phoneDevice.address,
          email: emailDevice.address
        },
        devices,
        p2aSourceId,
        advocateShortUrlId
      };
    },

    getAdvocateDevices(formValues) {
      const devices = {};
      const phone = this.normalizePhone(formValues.phone);
      const { email } = formValues;

      if (!_isEmpty(phone)) {
        devices.phone = {
          address: phone,
          previouslySubscribed: _get(
            this.advocateCreatePayload,
            "devices.phone.subscribed",
            formValues.subscribeSms
          ),
          subscribed: formValues.subscribeSms || false
        };
      }

      if (!_isEmpty(email)) {
        devices.email = {
          address: email,
          subscribed: formValues.subscribeEmail || false
        };
      }
      return devices;
    },
    markActionCompleted() {
      this.$store.dispatch("navigation/actionCompleted", this.currentAction);
      this.setCurrentMessage(0);
    },
    shouldCreateOrUpdateAdvocate(payload) {
      return !_isEqual(this.advocateCreatePayload, payload);
    },
    /**
     * Gets custom fields values from the state
     * keyed on the custom fields ids
     * @returns {{}}
     */
    getStateCustomFieldsValues() {
      const customFieldsValues = {};
      _forOwn(this.customFields, (fieldDetails, fieldName) => {
        const fieldValue = this.formValues[fieldName];
        customFieldsValues[fieldDetails.id] = fieldValue;
      });
      return customFieldsValues;
    },
    postTakeAction(action, advocateId, legislators, messages) {
      const devices = this.getAdvocateDevices(this.formValues);
      const { p2aSourceId } = this;

      const address = this.getAdvocateAddress(this.formValues);

      if (_isEmpty(address.zipcode)) {
        address.zipcode = this.loadStart;
      }

      const actionParameters = {
        action,
        advocateid: advocateId,
        legislators,
        messages,
        devices,
        p2aSourceId,
        address
      };

      if (
        action === "twitter" &&
        this.socialShare.shareEn &&
        this.formValues.shareWithFollowers &&
        typeof this.socialShare.shareTwitterBody !== "undefined"
      ) {
        actionParameters.share = 1;
        actionParameters.sharetwitterbody = formatParenthesis(this.socialShare.shareTwitterBody);
      }

      return AxiosTakeAction.post("", actionParameters);
    },
    twitterAction() {
      if (this.currentActionTaken) {
        return false;
      }

      const self = this;
      // If twitter credentials are already saved, go ahead and take action
      if (this.twitterCredentialsSaved) {
        return this.createPreviewsAndTakeAction();
      }

      const authorizeWindow = this.authorizeTwitter();

      // eslint-disable-next-line consistent-return
      const checkInterval = setInterval(() => {
        if (!authorizeWindow || !authorizeWindow.closed) {
          return false;
        }
        clearInterval(checkInterval);
        self.createPreviewsAndTakeAction();
      }, 500);
      return true;
    },
    sendTakeActionPostRequest() {
      const action = this.currentAction;
      const payload = this.transformLegislatorMessagesFromStore();

      return this.postTakeAction(
        action,
        this.advocateId,
        payload.legislators,
        payload.messages
      );
    },
    async createPreviewsAndTakeAction() {
      try {
        await this.createPreviews();
        return this.takeAction();
      } catch (error) {
        this.setFormError(this.axiosHelper.retrieveErrorMessage(error));
        this.toggleActionLoad(false);
        return this.errorMessage;
      }
    },
    // Try to save Twitter Credentials in the session if we have any for the advocate
    async handleStoreTwitterCredentials() {
      if (!this.shouldStoreTwitterCredentials) {
        return false;
      }
      const saveCredentialsParameters = {
        network: "TWITTER"
      };
      const saveCredentialsResponse = await SocialApi.post(
        "/token",
        saveCredentialsParameters
      );
      this.setTwitterCredentialsSaved(
        saveCredentialsResponse.data.credentialsSaved
      );
      return true;
    },
    // This transformer is needed to return a map of legislator => messages
    // The backend code is expecting this payload. We need to refactor that to expect the payload findLegislators returns.
    // Existing campaign templates are using this endpoint which is why a refactor is not easy.
    transformLegislatorMessagesFromStore() {
      const messages = {};
      const legislators = {};

      _forEach(this.legislatorMessages, legislatorMessage => {
        const messageId = legislatorMessage.message.id;
        const currentLegislators = legislatorMessage.legislators;
        let messageBody = legislatorMessage.message.body || null;
        messages[messageId] = {
          body: formatParenthesis(messageBody) || null,
          subject: formatParenthesis(legislatorMessage.message.subject) || null,
        };

        _forEach(currentLegislators, legislator => {
          const legIdObject = legislator.id;
          legislators[legIdObject.p2a] = messageId;
        });
      });

      return { messages, legislators };
    },
    skipStep() {
      this.eventsBus.$emit("skipStep");
    },
    async takeAction() {
      const action = this.currentAction;
      if (this.currentActionTaken) {
        return false;
      }

      try {
        const response = await this.sendTakeActionPostRequest();

        // continue load until call started event received
        if (this.currentAction !== "call") {
          this.toggleActionLoad(false);
        }

        return this.handleAfterTakeAction(response.data);
      } catch (error) {
        const errorMessage = this.axiosHelper.retrieveErrorMessage(error);

        if (this.currentAction !== "twitter") {
          this.setFormError(errorMessage);
        }

        this.showErrorModal();
        this.$store.dispatch("forms/actionError", errorMessage);
        this.eventsBus.$emit("actionFailed", {
          action,
          error: errorMessage
        });
        this.toggleActionLoad(false);
        return errorMessage;
      }
    },
    handleAfterTakeAction(responseData) {
      if (this.currentActionTaken) {
        return false;
      }

      this.$store.dispatch(
        "campaign/advocateGoalCounts",
        responseData.advocateGoalCounts
      );

      // When calling, prevent actionCompleted event from firing unless call statuses are all complete
      if (this.currentAction === "call" && this.callsAllCompleted !== true) {
        return false;
      }

      this.incrementStepInAction();

      this.markActionCompleted();
      return responseData;
    },
    assignSubmitHoverColor(showHover) {
      this.showButtonHover = showHover;
    },
    toggleActionLoad(bool) {
      this.$store.dispatch("navigation/actionLoading", bool);
    }
  }
};
</script>
